import { Injectable } from '@angular/core';
import { SalesDocument, SalesDocumentPosition, SalesDocumentType, Synced } from '@icc/b2b/shared';
import { ConfigService } from '@icc/shared';
import { core, ToastService } from '@icc/shared/helpers';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';
import { SalesDocumentService } from './sales-document.service';
import { SyncService } from './sync.service';
import { DocumentManagerService } from './document-manager.service';
import { DocumentCreationService } from './document-creation.service';

@Injectable({
    providedIn: 'root',
})
export class DocumentValuationService {
    constructor(
        private salesDocumentService: SalesDocumentService,
        private documentCreationService: DocumentCreationService,
        private documentManagerService: DocumentManagerService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private configService: ConfigService,
        private syncService: SyncService,
    ) {}

    async inquireValuation(document: SalesDocument) {
        let sourceDocumentPositions = await this.documentManagerService.getDocumentPosition(document.uuid) as SalesDocumentPosition[];
        sourceDocumentPositions = sourceDocumentPositions.filter(
            (position) => position.priceBeforeDiscountsValue === null ||
                Number.isNaN(position.priceBeforeDiscountsValue) ||
                typeof position.priceBeforeDiscountsValue === 'undefined'
        );
        this.toastService.add({
            title: this.translateService.instant('SALES_DOCUMENT|Tworzenie zapytania ofertowego'),
            body: this.translateService.instant('SALES_DOCUMENT|Zostanie utworzone i wysłane zapytanie ofertowe z oferty nr {offerKey} o wycenę {positionCount} pozycji.',
                {
                    offerKey: document.documentNumber.join(''),
                    positionCount: sourceDocumentPositions.length,
                }
            ),
        });
        const {inquire, positionsCount} = await this.createInquire(document);

        const result = await this.salesDocumentService.isDocumentSynced$(inquire.uuid)
            .pipe(
                filter((el) => el === Synced.Synced),
                take(1)
            )
            .toPromise();

        const email: SalesDocument['email'] = {
            uuid: core.generateUUID(),
            type: 'valuation',
            documentIdToSendEmail: inquire.uuid,
            neededDocumentsIds: {
                [inquire.uuid]: false,
            },
        };
        this.documentManagerService.saveDocument(inquire.uuid, {
            email,
        }, true);

        const syncedInquire = await this.documentManagerService.getDocument(inquire.uuid);

        this.toastService.add({
            title: this.translateService.instant('SALES_DOCUMENT|Zapytanie ofertowe zostało wysłana'),
            body: this.translateService.instant('SALES_DOCUMENT|Przesłano zapytanie ofertowe nr {offerKey} o wycenę {positionCount} pozycji.',
                {
                    offerKey: syncedInquire.documentNumber.join(''),
                    positionCount: positionsCount,
                }
            ),
        });
    }

    async createInquire(document: SalesDocument) {
        const {newDocument, newDocumentPositions} = await this.documentCreationService.duplicateDocument(
            document,
            false,
            false,
            false,
            SalesDocumentType.Inquiry,
        );
        return {
            inquire: newDocument,
            positionsCount: newDocumentPositions.length,
        }
    }

    async sendValuation(document: SalesDocument, validityDate: Date) {
        await this.syncService.addCurrentDocumentId(document.rootId);
        await this.syncService.waitForSynced([document.rootId, document.uuid]);
        this.documentManagerService.saveDocument(document.uuid, {
            isConfirmed: true,
            confirmerUserId: this.configService.user.user.id,
        });

        const valuationPositions = await this.documentManagerService.getDocumentPosition(document.uuid);

        let quotation = await this.documentManagerService.getDocument(document.rootId);
        let quotationPositions = await this.documentManagerService.getDocumentPosition(document.rootId);
        let quotationPositionsMap = new Map(quotationPositions.map(position => [position.uuid, position]));
        let quotationPositionsUuidMap = new Map(quotationPositions.map(position => [position.uuid, position.uuid]));

        if (quotation.type === 'sales' && quotation.isConfirmed) {
            const duplicated = await this.documentCreationService.duplicateDocument(
                quotation,
                true,
                false,
                false,
            );
            quotation = duplicated.newDocument;
            quotationPositions = duplicated.newDocumentPositions;
            quotationPositionsMap = new Map(quotationPositions.map(position => [position.uuid, position]));
            quotationPositionsUuidMap = duplicated.positionsMap;
        }

        if (quotation.type === 'sales') {
            for (const valuationPosition of valuationPositions as SalesDocumentPosition[]) {
                if (!isNaN(parseFloat(valuationPosition.priceBeforeDiscountsValue as any))) {
                    valuationPosition.validityDate = validityDate;
                    this.salesDocumentService.savePositionPrices(valuationPosition.uuid, document.uuid, {
                        validityDate,
                    });

                    const quotationPosition = quotationPositionsMap.get(quotationPositionsUuidMap.get(valuationPosition.rootId)) as SalesDocumentPosition;
                    if (quotationPosition) {
                        quotationPosition.configuration = valuationPosition.configuration;
                        quotationPosition.priceDiscounts = valuationPosition.priceDiscounts;
                        this.salesDocumentService.valuatePositionPrice(quotationPosition, quotation, validityDate);
                    }
                }
            }
        }
    }
}
